import React, { createContext, useContext, useState, useEffect } from 'react';
import { HashinalsWalletConnectSDK } from '@hashgraphonline/hashinal-wc';
import { LedgerId } from '@hashgraph/sdk';
import { groupNftsByCollection } from '../utils/nftUtils';

const HederaContext = createContext();

export const HederaProvider = ({ children }) => {
    const [sdk, setSdk] = useState(null);
    const [accountId, setAccountId] = useState(null);
    const [network, setNetwork] = useState(null);
    const [balance, setBalance] = useState(null);
    const [isConnecting, setIsConnecting] = useState(false);
    const [error, setError] = useState(null);
    const [isInitialized, setIsInitialized] = useState(false);
    const [nfts, setNfts] = useState([]);
    const [isLoadingNfts, setIsLoadingNfts] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [lastRefresh, setLastRefresh] = useState(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [collections, setCollections] = useState({});

    // Define fetchAllCollections at component level
    const fetchAllCollections = async () => {
        if (!accountId) return {};
        
        try {
            console.log('Fetching all collections first...');
            const baseUrl = 'https://mainnet-public.mirrornode.hedera.com';
            
            // Get all token relationships to identify all collections
            const tokensUrl = `/api/v1/accounts/${accountId}/tokens`;
            console.log('Fetching token relationships:', `${baseUrl}${tokensUrl}`);
            
            const tokenResponse = await fetch(`${baseUrl}${tokensUrl}`);
            if (!tokenResponse.ok) {
                console.error('Failed to fetch token relationships');
                return {};
            }
            
            const tokenData = await tokenResponse.json();
            console.log('Token relationships found:', tokenData.tokens.length);
            
            // Filter for NFT tokens only
            const nftTokens = tokenData.tokens.filter(token => token.type === 'NON_FUNGIBLE_UNIQUE');
            console.log('NFT collections found:', nftTokens.length);
            
            if (nftTokens.length === 0) {
                return {};
            }
            
            // Fetch detailed token info for each collection to get proper names
            const collectionPromises = nftTokens.map(async (token) => {
                try {
                    const tokenInfoUrl = `/api/v1/tokens/${token.token_id}`;
                    console.log('Fetching token info:', `${baseUrl}${tokenInfoUrl}`);
                    
                    const tokenInfoResponse = await fetch(`${baseUrl}${tokenInfoUrl}`);
                    if (tokenInfoResponse.ok) {
                        const tokenInfo = await tokenInfoResponse.json();
                        console.log(`=== TOKEN INFO DEBUG ===`);
                        console.log(`Token ID: ${token.token_id}`);
                        console.log(`Full token info response:`, tokenInfo);
                        console.log(`Token name field:`, tokenInfo.name);
                        console.log(`Token symbol field:`, tokenInfo.symbol);
                        console.log(`Token type field:`, tokenInfo.type);
                        console.log(`Collection name will be: "${tokenInfo.name || `Collection ${token.token_id}`}"`);
                        console.log(`========================`);
                        return {
                            collectionId: token.token_id,
                            name: tokenInfo.name || `Collection ${token.token_id}`,
                            symbol: tokenInfo.symbol || '',
                            count: token.balance,
                            nfts: []
                        };
                    } else {
                        console.error(`Failed to fetch token info for ${token.token_id}:`, tokenInfoResponse.status, tokenInfoResponse.statusText);
                    }
                } catch (err) {
                    console.error(`Error fetching token info for ${token.token_id}:`, err);
                }
                
                // Fallback if token info fetch fails
                return {
                    collectionId: token.token_id,
                    name: `Collection ${token.token_id}`,
                    count: token.balance,
                    nfts: []
                };
            });
            
            const collectionData = await Promise.all(collectionPromises);
            
            // Create collections object
            const collectionPlaceholders = {};
            collectionData.forEach(collection => {
                collectionPlaceholders[collection.collectionId] = collection;
            });
            
            console.log('Collections with proper names:', collectionPlaceholders);
            
            // Set collections immediately with proper names
            setCollections(collectionPlaceholders);
            
            // Return the collections data for use in fetchNFTs
            return collectionPlaceholders;
        } catch (err) {
            console.error('Error fetching all collections:', err);
            return {};
        }
    };

    // Initialize SDK
    useEffect(() => {
        const initSDK = async () => {
            try {
                // Try to get project ID from environment variable, fallback to hardcoded value if not found
                const projectId = process.env.REACT_APP_WALLET_CONNECT_PROJECT_ID || 'b7914e22160d1d8dbcec79787c3d915e';
                console.log('Project ID:', projectId);

                if (!projectId) {
                    throw new Error('WalletConnect Project ID is not defined');
                }

                const sdkInstance = HashinalsWalletConnectSDK.getInstance();
                console.log('Initializing SDK with project ID:', projectId);

                await sdkInstance.init(
                    projectId,
                    {
                        name: 'Hash Gallery',
                        description: '3D NFT Gallery Space',
                        url: window.location.origin,
                        icons: [`${window.location.origin}/logo192.png`]
                    },
                    LedgerId.MAINNET
                );

                console.log('SDK initialized successfully');
                setSdk(sdkInstance);
                setIsInitialized(true);

                // Auto-connect removed - wallet will only connect when user clicks connect button

            } catch (err) {
                console.error('\n Failed to initialize Hedera SDK:', err);
                setError('Failed to initialize Hedera connection');
            }
        };

        initSDK();
    }, []);

    // Remove the separate useEffect for fetchAllCollections since it's now called within fetchNFTs
    // useEffect(() => {
    //     if (accountId) {
    //         fetchAllCollections();
    //     }
    // }, [accountId]);

    const fetchSvgContent = async (hcsUrl) => {
        try {
            const match = hcsUrl.match(/hcs:\/\/1\/(\d+\.\d+\.\d+)/);
            if (!match) {
                throw new Error('Invalid HCS URL format');
            }
            const topicId = match[1];

            const metadataResponse = await fetch(`https://kiloscribe.com/api/inscription-cdn/${topicId}`);
            if (!metadataResponse.ok) {
                throw new Error(`HTTP error! status: ${metadataResponse.status}`);
            }
            const metadata = await metadataResponse.json();

            if (metadata.image && metadata.image.startsWith('hcs://')) {
                const imageMatch = metadata.image.match(/hcs:\/\/1\/(\d+\.\d+\.\d+)/);
                if (imageMatch) {
                    const imageTopicId = imageMatch[1];
                    const imageResponse = await fetch(`https://kiloscribe.com/api/inscription-cdn/${imageTopicId}`);
                    if (imageResponse.ok) {
                        const svgContent = await imageResponse.text();
                        return {
                            svgContent,
                            metadata
                        };
                    }
                }
            }

            return { metadata };
        } catch (error) {
            console.error('Error fetching SVG:', error);
            return null;
        }
    };

    // Fetch NFTs when account connects
    useEffect(() => {
        const fetchNFTs = async () => {
            if (!accountId) return;

            try {
                setIsLoadingNfts(true);
                setError(null);
                
                // First, ensure we have collection info with proper names
                console.log('Fetching collections before NFTs...');
                const collectionInfo = await fetchAllCollections();
                console.log('Collections fetched, now fetching NFTs...');
                
                // Load ALL NFTs at once
                const baseUrl = 'https://mainnet-public.mirrornode.hedera.com';
                let nextLink = `/api/v1/accounts/${accountId}/nfts?limit=100`;
                let allNfts = [];
                let batchCount = 0;
                const maxBatches = 20; // Safety limit to prevent infinite loops
                
                // Keep fetching until there are no more NFTs or we reach the limit
                while (nextLink && batchCount < maxBatches) {
                    batchCount++;
                    console.log(`Fetching NFT batch #${batchCount}:`, `${baseUrl}${nextLink}`);
                    
                    const response = await fetch(`${baseUrl}${nextLink}`);
                    if (!response.ok) {
                        throw new Error('Failed to fetch NFTs from Mirror Node');
                    }
                    
                    const data = await response.json();
                    console.log(`Batch #${batchCount} response:`, {
                        nftsReceived: data.nfts.length,
                        hasNextPage: !!data.links?.next
                    });
                    
                    // Process this batch
                    const processedNfts = await Promise.all(
                        data.nfts.map(async (nft) => {
                            try {
                                const decodedMetadata = atob(nft.metadata);
                                
                                // Handle SVG NFTs
                                if (nft.token_id === '0.0.7303644') {
                                    const result = await fetchSvgContent(decodedMetadata);
                                    return {
                                        ...nft,
                                        decodedMetadata: result?.svgContent,
                                        metadata: result?.metadata,
                                        isImage: true
                                    };
                                }

                                // Check if it's an HCS URL
                                if (decodedMetadata.startsWith('hcs://')) {
                                    console.log('Processing HCS URL:', decodedMetadata);
                                    const match = decodedMetadata.match(/hcs:\/\/(\d+)\/(\d+\.\d+\.\d+)/);
                                    if (match) {
                                        const topicId = match[2];
                                        try {
                                            const response = await fetch(`https://kiloscribe.com/api/inscription-cdn/${topicId}`);
                                            if (response.ok) {
                                                const content = await response.text();

                                                // Check if content starts with glTF magic bytes
                                                if (content.startsWith('glTF')) {
                                                    console.log('Found binary GLB data in HCS content');
                                                    // Create blob URL for the GLB data
                                                    const blob = await response.blob();
                                                    const blobUrl = URL.createObjectURL(blob);

                                                    return {
                                                        ...nft,
                                                        metadata: {
                                                            name: `NFT #${nft.serial_number}`,
                                                            description: 'GLB 3D Model'
                                                        },
                                                        isImage: false,
                                                        isModel: true,
                                                        modelUrl: blobUrl,
                                                        url: blobUrl
                                                    };
                                                }

                                                console.log('HCS content fetched:', {
                                                    tokenId: nft.token_id,
                                                    content: content.substring(0, 100) // First 100 chars for logging
                                                });

                                                // Try to parse as JSON first
                                                try {
                                                    const metadata = JSON.parse(content);

                                                    // If metadata has an HCS URL for image
                                                    if (metadata.image && metadata.image.startsWith('hcs://')) {
                                                        const imageMatch = metadata.image.match(/hcs:\/\/(\d+)\/(\d+\.\d+\.\d+)/);
                                                        if (imageMatch) {
                                                            console.log('Converting HCS URL to direct URL:', metadata.image);
                                                            // Convert HCS URL to direct URL
                                                            metadata.image = `https://kiloscribe.com/api/inscription-cdn/${imageMatch[2]}`;
                                                            console.log('Converted to:', metadata.image);

                                                            // Check if it's a GLB/GLTF model
                                                            if (metadata.type === 'model/gltf-binary' ||
                                                                metadata.type === 'model/gltf+json') {
                                                                console.log('Detected 3D model from metadata type');
                                                                return {
                                                                    ...nft,
                                                                    metadata,
                                                                    isImage: false,
                                                                    isModel: true,
                                                                    modelUrl: metadata.image,
                                                                    url: metadata.image
                                                                };
                                                            }
                                                        }
                                                    }

                                                    // Process files array for HIP-412 multi-file NFTs
                                                    let processedFiles = [];
                                                    let hasMultipleFiles = false;
                                                    
                                                    if (metadata.files && Array.isArray(metadata.files)) {
                                                        console.log('Processing HIP-412 files array:', metadata.files);
                                                        
                                                        processedFiles = await Promise.all(
                                                            metadata.files.map(async (file) => {
                                                                let processedFile = { ...file };
                                                                
                                                                // Convert HCS URLs in files array
                                                                if (file.uri && file.uri.startsWith('hcs://')) {
                                                                    const fileMatch = file.uri.match(/hcs:\/\/(\d+)\/(\d+\.\d+\.\d+)/);
                                                                    if (fileMatch) {
                                                                        processedFile.uri = `https://kiloscribe.com/api/inscription-cdn/${fileMatch[2]}`;
                                                                        console.log('Converted file URI:', file.uri, '->', processedFile.uri);
                                                                    }
                                                                }
                                                                
                                                                return processedFile;
                                                            })
                                                        );
                                                        
                                                        hasMultipleFiles = processedFiles.length > 0;
                                                        console.log('Processed files:', processedFiles);
                                                    }

                                                    return {
                                                        ...nft,
                                                        metadata,
                                                        files: processedFiles,
                                                        hasMultipleFiles,
                                                        isImage: !metadata.type?.includes('model') && !metadata.type?.startsWith('video/'),
                                                        isModel: metadata.type?.includes('model'),
                                                        isVideo: metadata.type?.startsWith('video/'),
                                                        modelUrl: metadata.type?.includes('model') ? metadata.image : null,
                                                        url: metadata.image
                                                    };
                                                } catch {
                                                    // If not JSON, use content directly
                                                    const isGlb = content &&
                                                        (content.toLowerCase().endsWith('.glb') ||
                                                            content.toLowerCase().endsWith('.gltf'));

                                                    return {
                                                        ...nft,
                                                        metadata: {
                                                            name: `NFT #${nft.serial_number}`,
                                                            image: content
                                                        },
                                                        isImage: !isGlb,
                                                        isModel: isGlb,
                                                        modelUrl: isGlb ? content : null,
                                                        url: content
                                                    };
                                                }
                                            }
                                        } catch (err) {
                                            console.error('Error processing HCS content:', err);
                                        }
                                    }
                                }

                                // Handle other NFTs
                                try {
                                    // First try to fetch metadata if it's a URL
                                    if (decodedMetadata.startsWith('http') || decodedMetadata.startsWith('ipfs')) {
                                        let metadataUrl = decodedMetadata;
                                        if (decodedMetadata.startsWith('ipfs')) {
                                            metadataUrl = `https://ipfs.io/ipfs/${decodedMetadata.replace('ipfs://', '')}`;
                                        }

                                        const metadataResponse = await fetch(metadataUrl);
                                        if (metadataResponse.ok) {
                                            const metadata = await metadataResponse.json();
                                            // Convert IPFS image URLs
                                            if (metadata.image && metadata.image.startsWith('ipfs://')) {
                                                metadata.image = `https://ipfs.io/ipfs/${metadata.image.replace('ipfs://', '')}`;
                                            }
                                            
                                            // Process files array for HIP-412 multi-file NFTs
                                            let processedFiles = [];
                                            let hasMultipleFiles = false;
                                            
                                            if (metadata.files && Array.isArray(metadata.files)) {
                                                processedFiles = metadata.files.map(file => {
                                                    let processedFile = { ...file };
                                                    
                                                    // Convert IPFS URLs in files array
                                                    if (file.uri && file.uri.startsWith('ipfs://')) {
                                                        processedFile.uri = `https://ipfs.io/ipfs/${file.uri.replace('ipfs://', '')}`;
                                                    }
                                                    
                                                    return processedFile;
                                                });
                                                
                                                hasMultipleFiles = processedFiles.length > 0;
                                            }
                                            
                                            // Check if the image is a GLB file
                                            const isGlb = metadata.image &&
                                                (metadata.image.toLowerCase().endsWith('.glb') ||
                                                    metadata.image.toLowerCase().endsWith('.gltf'));
                                            return {
                                                ...nft,
                                                metadata,
                                                files: processedFiles,
                                                hasMultipleFiles,
                                                isImage: !isGlb && !metadata.type?.startsWith('video/'),
                                                isModel: isGlb,
                                                isVideo: metadata.type?.startsWith('video/')
                                            };
                                        }
                                    }

                                    // If not a URL or fetch failed, try parsing as JSON
                                    try {
                                        const metadata = JSON.parse(decodedMetadata);
                                        if (metadata.image && metadata.image.startsWith('ipfs://')) {
                                            metadata.image = `https://ipfs.io/ipfs/${metadata.image.replace('ipfs://', '')}`;
                                        }
                                        
                                        // Process files array for HIP-412 multi-file NFTs
                                        let processedFiles = [];
                                        let hasMultipleFiles = false;
                                        
                                        if (metadata.files && Array.isArray(metadata.files)) {
                                            processedFiles = metadata.files.map(file => {
                                                let processedFile = { ...file };
                                                
                                                // Convert IPFS URLs in files array
                                                if (file.uri && file.uri.startsWith('ipfs://')) {
                                                    processedFile.uri = `https://ipfs.io/ipfs/${file.uri.replace('ipfs://', '')}`;
                                                }
                                                
                                                return processedFile;
                                            });
                                            
                                            hasMultipleFiles = processedFiles.length > 0;
                                        }
                                        
                                        // Check if the image is a GLB file
                                        const isGlb = metadata.image &&
                                            (metadata.image.toLowerCase().endsWith('.glb') ||
                                                metadata.image.toLowerCase().endsWith('.gltf'));

                                        console.log('NFT Metadata processing:', {
                                            tokenId: nft.token_id,
                                            imageUrl: metadata.image,
                                            isGlb,
                                            hasMultipleFiles,
                                            filesCount: processedFiles.length,
                                            metadata
                                        });

                                        return {
                                            ...nft,
                                            metadata,
                                            files: processedFiles,
                                            hasMultipleFiles,
                                            isImage: !isGlb && !metadata.type?.startsWith('video/'),
                                            isModel: isGlb,
                                            isVideo: metadata.type?.startsWith('video/'),
                                            modelUrl: isGlb ? metadata.image : null
                                        };
                                    } catch {
                                        // If not JSON, use the decoded metadata as is
                                        return {
                                            ...nft,
                                            metadata: {
                                                name: `NFT #${nft.serial_number}`,
                                                description: decodedMetadata
                                            },
                                            isImage: false
                                        };
                                    }
                                } catch (err) {
                                    console.error(`Failed to process metadata for NFT ${nft.token_id}:`, err);
                                    return {
                                        ...nft,
                                        metadata: {
                                            name: `NFT #${nft.serial_number}`,
                                            description: decodedMetadata
                                        },
                                        isImage: false
                                    };
                                }
                            } catch (err) {
                                console.error(`Failed to decode metadata for NFT ${nft.token_id}:`, err);
                                return nft;
                            }
                        })
                    );
                    
                    // Add this batch to our total
                    allNfts = [...allNfts, ...processedNfts];
                    
                    // Set NFTs and update collections with each batch
                    setNfts(allNfts);
                    
                    try {
                        console.log(`Updating collections after batch #${batchCount}...`);
                        const groupedCollections = groupNftsByCollection(allNfts, collectionInfo);
                        setCollections(groupedCollections);
                    } catch (err) {
                        console.error('Error grouping NFTs by collection:', err);
                    }
                    
                    // Check if there are more NFTs to fetch
                    if (data.links?.next) {
                        nextLink = data.links.next;
                    } else {
                        nextLink = null;
                    }
                }
                
                console.log(`Finished loading all NFTs: ${allNfts.length} NFTs in ${batchCount} batches`);
                
                // Final collection grouping
                try {
                    console.log('Final grouping of all NFTs by collection...');
                    console.log('Using collection info from fetchAllCollections:', collectionInfo);
                    const groupedCollections = groupNftsByCollection(allNfts, collectionInfo);
                    console.log('Grouped collections result:', groupedCollections);
                    setCollections(groupedCollections);
                    setHasMore(false); // We've loaded everything
                } catch (err) {
                    console.error('Error grouping NFTs by collection:', err);
                }
                
                setLastRefresh(Date.now());
            } catch (err) {
                console.error('Failed to fetch NFTs:', err);
                setError('Failed to fetch NFTs');
            } finally {
                setIsLoadingNfts(false);
            }
        };

        if (accountId) {
            fetchNFTs();
        }
    }, [accountId]);

    // No need to load more since we're loading everything upfront
    const loadMoreNFTs = async () => {
        console.log("loadMoreNFTs called, but no action needed - all NFTs already loaded");
        return; // Do nothing, we've already loaded everything
    };

    // Connect wallet
    const connect = async () => {
        if (!sdk || !isInitialized) {
            console.error('SDK not initialized');
            setError('SDK not initialized');
            return;
        }

        try {
            setIsConnecting(true);
            setError(null);

            console.log('Attempting to connect wallet...');
            const session = await sdk.connect();
            console.log('Wallet connected, fetching account info...');

            const accountInfo = await sdk.getAccountInfo();
            const accountBalance = await sdk.getAccountBalance();

            console.log('Account info retrieved:', accountInfo);

            setAccountId(accountInfo.accountId);
            setNetwork(accountInfo.network);
            setBalance(accountBalance);

            return session;
        } catch (err) {
            console.error('Failed to connect wallet:', err);
            setError('Failed to connect wallet');
            throw err;
        } finally {
            setIsConnecting(false);
        }
    };

    const disconnect = async () => {
        if (!sdk) return;

        try {
            await sdk.disconnect();
            setAccountId(null);
            setNetwork(null);
            setBalance(null);
            setNfts([]);
        } catch (err) {
            console.error('Failed to disconnect wallet:', err);
            setError('Failed to disconnect wallet');
        }
    };

    const value = {
        sdk,
        accountId,
        network,
        balance,
        isConnecting,
        error,
        isInitialized,
        nfts,
        isLoadingNfts,
        isLoadingMore,
        hasMore,
        loadMoreNFTs,
        connect,
        disconnect,
        collections
    };

    return (
        <HederaContext.Provider value={value}>
            {children}
        </HederaContext.Provider>
    );
};

export const useHedera = () => {
    const context = useContext(HederaContext);
    if (context === undefined) {
        throw new Error('useHedera must be used within a HederaProvider');
    }
    return context;
}; 